import { assertValidFilename } from '../utils/fileUtils.js';
// Use chunk sizes that can be diviced by 2 without decimals
// 1MB =  1024 * 1024 * 1 = 1048576 bytes
// 10MB =  1024 * 1024 * 10 = 10485760 bytes
// 100kt =  1024 * 1024 * 1 / 8 = 131072 bytes
// const DEFAULT_CHUNK_SIZE = (1024 * 1024 * 1) / 8; // 100kt for testing chunks
const DEFAULT_CHUNK_SIZE = 1024 * 1024 * 2; // 2MB
const DEFAULT_CHUNK_THRESHOLD = DEFAULT_CHUNK_SIZE * 5; // 10MB
export default class FileClient {
    static service = 'file-system.file';
    apiClient;
    options = {
        chunkSize: DEFAULT_CHUNK_SIZE,
        chunkingThreshold: DEFAULT_CHUNK_THRESHOLD,
    };
    constructor(apiClient, options) {
        this.apiClient = apiClient;
        this.options = { ...this.options, ...options };
    }
    get chunkSize() {
        return (this.options.chunkSize ||
            this.apiClient.getOption('uploadChunkSize') ||
            DEFAULT_CHUNK_SIZE);
    }
    set chunkSize(size) {
        this.options.chunkSize = size;
    }
    get chunkingThreshold() {
        return this.options.chunkingThreshold || DEFAULT_CHUNK_THRESHOLD;
    }
    set chunkingThreshold(threshold) {
        this.options.chunkingThreshold = threshold;
    }
    create(params) {
        const endpoint = this.makeEndpoint('create');
        return this.apiClient.exec(endpoint, params);
    }
    update(params) {
        const endpoint = this.makeEndpoint('update');
        return this.apiClient.exec(endpoint, params);
    }
    async read(params) {
        const endpoint = this.makeEndpoint('read');
        return this.apiClient.exec(endpoint, params);
    }
    write(params) {
        const endpoint = this.makeEndpoint('write');
        return this.apiClient.exec(endpoint, params);
    }
    async upload({ asset, path, specs, options, }, onProgress, signal) {
        if (specs?.name !== undefined) {
            throw new Error('specs.name is prohibited, filename should be provided in the "path" argument');
        }
        const { matchExistingFileWithName, ...rest } = options || {};
        const apiOptions = Object.keys(rest).length ? rest : undefined;
        const pathItems = path.split('/');
        const filename = pathItems.pop();
        let targetPath = pathItems.join('/');
        if (matchExistingFileWithName) {
            targetPath += '/' + matchExistingFileWithName;
            specs = { ...specs, name: filename };
        }
        else {
            targetPath += '/' + filename;
        }
        assertValidFilename(filename);
        if (asset.size < this.chunkingThreshold) {
            const data = [];
            data.push({
                name: 'file',
                value: (await Promise.resolve(asset.slice())),
                filename,
            });
            data.push({
                name: 'q',
                value: JSON.stringify({ path: targetPath, specs, options: apiOptions }),
            });
            const endpoint = 'file-system.file-http/upload';
            onProgress && onProgress({ loaded: 0, total: asset.size });
            const result = await this.apiClient.upload(endpoint, data, onProgress, signal);
            onProgress && onProgress({ loaded: asset.size, total: asset.size });
            return result;
        }
        else {
            return this.uploadChunks({
                asset,
                path: targetPath,
                filename,
                specs,
                options: apiOptions,
            }, onProgress, signal);
        }
    }
    async uploadChunks({ asset, filename, ...rest }, onProgress, signal) {
        const endpoint = 'file-system.file-http/chunked-upload';
        // Fetch sizes of each file
        const chunkSize = this.chunkSize;
        const lastChunkSize = asset.size % chunkSize || chunkSize;
        const totalSize = asset.size;
        const chunks = Math.ceil(asset.size / chunkSize);
        let totalLoaded = 0;
        let start = 0;
        let result = null;
        onProgress && onProgress({ loaded: 0, total: asset.size });
        for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
            const end = Math.min(start + chunkSize, asset.size);
            const chunk = await Promise.resolve(asset.slice(start, end));
            start = end;
            const data = [];
            data.push({ name: 'file', value: chunk, filename });
            data.push({
                name: 'q',
                value: JSON.stringify({
                    ...rest,
                    chunks,
                    chunk: chunkIndex,
                }),
            });
            const chunkResult = await this.apiClient.upload(endpoint, data, ({ loaded }) => {
                onProgress &&
                    onProgress({ loaded: totalLoaded + loaded, total: totalSize });
            }, signal);
            totalLoaded += chunkIndex === chunks - 1 ? lastChunkSize : chunkSize;
            onProgress && onProgress({ loaded: totalLoaded, total: totalSize });
            // Last request should have given us a proper result
            if (typeof chunkResult === 'string') {
                result = chunkResult;
            }
        }
        if (result === null) {
            throw new Error('Upload failed; invalid server response');
        }
        return result;
    }
    makeEndpoint(operation) {
        return `${FileClient.service}/${operation}`;
    }
}
